import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { cn } from './cn';

interface DrawerProps {
	open: boolean;
	onClose: () => void;
	title?: React.ReactNode;
	side?: 'left' | 'right';
	children: React.ReactNode;
	className?: string;
}

/**
 * Side drawer — used for the mobile nav and for "details on click" panes.
 * On screens narrower than `sm` we expand to full-width; on tablet+ it
 * caps at 24rem.
 */
export function Drawer({
	open,
	onClose,
	title,
	side = 'left',
	children,
	className,
}: DrawerProps) {
	const enterFrom =
		side === 'left' ? '-translate-x-full' : 'translate-x-full';

	return (
		<Transition show={open} as={React.Fragment}>
			<Dialog onClose={onClose} className="relative z-50">
				<Transition.Child
					as={React.Fragment}
					enter="transition-opacity ease-out duration-200"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="transition-opacity ease-in duration-150"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" />
				</Transition.Child>

				<div className="fixed inset-0 overflow-hidden">
					<div className={cn('fixed inset-y-0 flex max-w-full', side === 'left' ? 'left-0' : 'right-0')}>
						<Transition.Child
							as={React.Fragment}
							enter="transform transition ease-out duration-300"
							enterFrom={enterFrom}
							enterTo="translate-x-0"
							leave="transform transition ease-in duration-200"
							leaveFrom="translate-x-0"
							leaveTo={enterFrom}
						>
							<Dialog.Panel
								className={cn(
									'relative flex h-full w-screen max-w-sm flex-col bg-white shadow-modal',
									className
								)}
							>
								{title && (
									<div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
										<Dialog.Title className="text-base font-semibold text-ink">
											{title}
										</Dialog.Title>
										<button
											onClick={onClose}
											aria-label="Close"
											className="rounded-md p-1 text-ink-muted hover:bg-slate-100 hover:text-ink"
										>
											<XMarkIcon className="h-5 w-5" />
										</button>
									</div>
								)}
								<div className="flex-1 overflow-y-auto scrollbar-thin">
									{children}
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}

export default Drawer;
